/* eslint-disable camelcase */
import { ElementRef, Injectable } from '@angular/core';
import { CSTField, CustomFieldDataObject } from './models/custom-data-field.model';
import { Observable, Subject, BehaviorSubject, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { System } from '../../shared-components/System';
import { CustomFieldDataType, CustomFieldDataTypeEnum } from './models/custom-data-field-type.model';
import { CustomFieldControlType } from './models/custom-data-control-type.model';
import { CustomDataModule } from './models/custom-data-module.model';
import { CustomDataObject } from './models/custom-data-object.model';
import { DataDefinitionList, DataDefinition } from '../../admin360/data-definition/data-definition.model';
import { map, take } from 'rxjs/operators';
import { CSTCustomPage } from './models/cst-custom-page.model';
import { SharedService } from '../../shared-components/shared.service';
import { ErrorList } from '../../shared-components/controls/error-prompt/error-list.model';
import { YearService } from '../../shared-components/year/year.service';
import { SecurityService } from '../../shared-components/security/security.service';
import { EFormDataObject } from '../../shared-components/e-forms/e-formDataObject.model';
import { EFormService } from '../../shared-components/e-forms/e-form.service';
import { EFormControl } from '../../shared-components/e-forms/e-form-control.model';
import { EFormRow } from '../../shared-components/e-forms/e-form-row.model';
import { CustomDesignPage } from '../../admin360/customization/models/custom-design-page.model';
import * as _ from 'lodash';
import { CSTImportResults } from './models/cst-import-results.model';
import { LoginService } from '../login/login.service';
import { EForm } from '../../shared-components/e-forms/e-form.model';

@Injectable()
export class CustomizationFrameworkService {

    customDataFields: CSTField[] = [];
    dataTypes: CustomFieldDataType[] = [];
    controlTypes: CustomFieldControlType[] = [];
    modules: CustomDataModule[] = [];
    objects: CustomDataObject[] = [];
    designPageIndexIncrementor = 1;
    basePageOnly = false;
    dataDefinitionLists: DataDefinitionList[] = [];
    dataDefinitionListValues: { [enum_value: string]: DataDefinition[] } = {};
    dataDefSubjects: { [enum_value: string]: Subject<DataDefinition[]> } = {};
    dataDefListSubjects: { [enum_value: string]: Subject<DataDefinitionList> } = {};
    dataStores: { [index: number]: CustomFieldDataObject[] } = {};

    public observablesBasePageOnly = new Subject<boolean>();
    observablesBasePageOnlyStream$ = this.observablesBasePageOnly.asObservable();

    public observableCustomDataFieldValueChanged = new Subject<CSTField>();
    observableCustomDataFieldValueChangedStream$ = this.observableCustomDataFieldValueChanged.asObservable();

    public observableFieldMetaDataRetrieved = new Subject<CSTField[]>();
    observableobservableFieldMetaDataRetrievedStream$ = this.observableFieldMetaDataRetrieved.asObservable();

    public observableFieldValuesRetrieved = new Subject<CSTField[]>();
    observableFieldValuesRetrievedStream$ = this.observableFieldValuesRetrieved.asObservable();


    constructor(
        private http: HttpClient,
        private sharedService: SharedService,
        private yearService: YearService,
        private securityService: SecurityService,
        private eFormService: EFormService,
        private loginService: LoginService
    ) {
        this.sharedService.observableClearStoredCustomFieldDataStream$.subscribe(o => this.clearAllStoredCustomFieldData());
    }

    updateBasePageOnly(basePageOnly: boolean) {
        this.basePageOnly = basePageOnly;
        this.observablesBasePageOnly.next(basePageOnly);
    }

    customDataFieldValueChanged(field: CSTField) {
        this.observableCustomDataFieldValueChanged.next(field);
    }

    getCustomDataFields(yearId: number, siteYearId: number): Observable<CSTField[]> {
        const queryString = '?yearId=' + yearId
            + '&siteYearId=' + siteYearId;
        return this.http.get<CSTField[]>(System.apiURL.Admin + 'GetCustomDataFields' + queryString);
    }

    getCustomDataFieldByID(fieldID: number): Observable<CSTField> {
        const queryString = '?fieldID=' + fieldID;
        return this.http.get<CSTField>(System.apiURL.Admin + 'GetCustomDataFieldByID' + queryString);
    }

    getCustomDataFieldByFieldName(fieldName: string): Observable<CSTField> {
        const queryString = '?fieldName=' + fieldName;
        return this.http.get<CSTField>(System.apiURL.Admin + 'GetCustomDataFieldByFieldName' + queryString);
    }

    getCustomFieldDataTypes(): Observable<CustomFieldDataType[]> {
        return this.http.get<CustomFieldDataType[]>(System.apiURL.Admin + 'GetCustomFieldDataTypes');
    }

    getCustomFieldControlTypes(): Observable<CustomFieldControlType[]> {
        return this.http.get<CustomFieldControlType[]>(System.apiURL.Admin + 'GetCustomFieldControlTypes');
    }

    getCustomDataFieldsByObjectID(objectID: number): Observable<CSTField[]> {
        return this.sharedService.getCustomDataFieldsByObjectID(objectID);
    }

    getCustomDataFieldValue(fieldID: number, masterId: number, siteYearId: number): Observable<string> {
        const queryString = '?fieldID=' + fieldID
            + '&masterId=' + masterId
            + '&siteYearId=' + siteYearId;
        return this.http.get<string>(System.apiURL.Admin + 'GetCustomDataFieldValue' + queryString);
    }

    updateCustomDataFieldValue(fieldID: number, masterId: number, textValue: string, siteYearId: number): Observable<boolean> {
        const queryString = '?fieldID=' + fieldID
            + '&masterId=' + masterId
            + '&textValue=' + textValue
            + '&siteYearId=' + siteYearId;
        return this.http.get<boolean>(System.apiURL.Admin + 'UpdateCustomDataFieldValue' + queryString);
    }

    getCSTModules(): Observable<CustomDataModule[]> {
        return this.http.get<CustomDataModule[]>(System.apiURL.Admin + 'GetCSTModules');
    }

    getCSTObjects(): Observable<CustomDataObject[]> {
        return this.http.get<CustomDataObject[]>(System.apiURL.Admin + 'GetCSTObjects');
    }

    getDataDefinitionList(yearId: number, siteId: number): Observable<DataDefinitionList[]> {
        const queryString = '?yearId=' + yearId +
            '&siteId=' + siteId;
        return this.http.get<DataDefinitionList[]>(System.apiURL.Admin + 'GetDataDefinitionList' + queryString);
    }

    getDataDefinitionListsWithCodes(yearId: number, siteYearId: number, masterId: number): Observable<DataDefinitionList[]> {
        const queryString = '?yearId=' + yearId +
            '&siteYearId=' + siteYearId +
            '&masterId=' + masterId;
        return this.http.get<DataDefinitionList[]>(System.apiURL.Admin + 'GetDataDefinitionListsWithCodes' + queryString);
    }

    getDataDefinitionListByEnum(
        enum_value: string,
        yearId = this.yearService.defaultYear.Id,
        siteYearId = this.yearService.defaultYear.SiteYearId,
        masterId: number = null
    ): Observable<DataDefinitionList> {
        let dataDefList: DataDefinitionList;
        // check if we have cached the data def list
        if (this.dataDefinitionLists) {
            dataDefList = this.dataDefinitionLists.find(o => o.Enum == enum_value);
        }
        if (dataDefList) {  // we have cached this list
            // check if we have also cached the data def list options
            if (this.dataDefinitionListValues && this.dataDefinitionListValues[enum_value] && this.dataDefinitionListValues[enum_value].filter(o => o.YearId == yearId && o.SiteYearId == siteYearId && o.MasterId == masterId).length > 0) {
                // we have cached the data def list options.
                dataDefList.DataDefinitions = this.dataDefinitionListValues[enum_value].filter(o => o.YearId == yearId && o.SiteYearId == siteYearId && o.MasterId == masterId);
                return of(dataDefList);
            }
            else {
                // we have not cached the data def list options.  We need to look them up.
                return this.getDataDefinition(enum_value, yearId, siteYearId, masterId).pipe(take(1)).pipe(map(dataDefs => {
                    dataDefList.DataDefinitions = dataDefs;
                    if (!this.dataDefinitionListValues[enum_value]) {
                        this.dataDefinitionListValues[enum_value] = [];
                    }
                    if (dataDefs) {
                        dataDefs.forEach(dd => {
                            if (!this.dataDefinitionListValues[enum_value].find(o => o.Id == dd.Id)) {
                                this.dataDefinitionListValues[enum_value].push(dd); // cache them for future use
                            }
                        });
                    }
                    return dataDefList;
                }));
            }
        }
        else {
            // we have not cached the list.  This call could be made multiple times if the same data def is used multiple times on the same page.  We will make a single
            // API call and use that same call for all subscriptions
            let sub = this.dataDefListSubjects[enum_value];
            if (!sub) {
                sub = new Subject<DataDefinitionList>();
                this.dataDefListSubjects[enum_value] = sub;
                const queryString = '?enum_value=' + enum_value +
                    '&yearId=' + yearId +
                    '&siteYearId=' + siteYearId +
                    '&masterId=' + masterId;
                const obs = this.http.get<DataDefinitionList>(System.apiURL.Admin + 'GetDataDefinitionListByEnum' + queryString);
                obs.pipe(take(1)).subscribe(o => {
                    sub.next(o);
                    // cache the data
                    if (o) {
                        this.dataDefinitionLists.push(o);
                        if (o.DataDefinitions) {
                          if (!this.dataDefinitionListValues[enum_value]) {
                              this.dataDefinitionListValues[enum_value] = [];
                          }
                          this.dataDefinitionListValues[enum_value].concat(o.DataDefinitions);
                      }
                    }
                    this.dataDefListSubjects[enum_value] = null;
                });
            }
            return sub.asObservable().pipe(take(1));
        }
    }

    getDataDefinition(
        enum_value: string,
        yearId = this.yearService.defaultYear.Id,
        siteYearId = this.yearService.defaultYear.SiteYearId,
        masterId: number = null
    ): Observable<DataDefinition[]> {
        let dataDefListValues: DataDefinition[];
        // check if we have cached the data def list
        if (this.dataDefinitionListValues && this.dataDefinitionListValues[enum_value]) {
            dataDefListValues = this.dataDefinitionListValues[enum_value].filter(o => o.YearId == yearId && o.SiteYearId == siteYearId && o.MasterId == masterId);
        }
        if (dataDefListValues && dataDefListValues.length > 0) {  // we have cached this list
            return of(dataDefListValues);
        }
        else {
            // we have not cached the list.  This call could be made multiple times if the same data def is used multiple times on the same page.  We will make a single
            // API call and use that same call for all subscriptions
            let bh = this.dataDefSubjects[enum_value];
            if (!bh) {
                bh = new Subject<DataDefinition[]>();
                this.dataDefSubjects[enum_value] = bh;
                let queryString = '';
                queryString += '?enum_value=' + enum_value +
                    '&yearId=' + yearId +
                    '&siteYearId=' + siteYearId +
                    '&masterId=' + masterId;
                const obs = this.http.get<DataDefinition[]>(System.apiURL.Admin + 'GetDataDefinition' + queryString);
                obs.pipe(take(1)).subscribe(o => {
                    bh.next(o);
                    if (o) {
                        if (!this.dataDefinitionListValues[enum_value]) {
                            this.dataDefinitionListValues[enum_value] = [];
                        }
                        this.dataDefinitionListValues[enum_value].concat(o);  // cache the data
                    }
                    this.dataDefSubjects[enum_value] = null;
                });
            }
            return bh.asObservable().pipe(take(1));
        }
    }

    importCustomFieldsFromClassic(): Observable<CSTImportResults> {
        return this.http.get<CSTImportResults>(System.apiURL.Admin + 'ImportCustomFieldsFromClassic');
    }

    checkClassicFieldsForDuplicateNames(): Observable<boolean> {
        return this.http.get<boolean>(System.apiURL.Admin + 'CheckClassicFieldsForDuplicateNames');
    }

    getCustomPagesByModuleObject(moduleId: number, objectId: number, siteYearId: number, area?: string): Observable<CSTCustomPage[]> {
        const queryString = '?moduleId=' + moduleId +
            '&objectId=' + objectId +
            '&siteYearId=' + siteYearId +
            '&area=' + area;
        return this.http.get<CSTCustomPage[]>(System.apiURL.Admin + 'GetCustomPagesByModuleObject' + queryString);
    }

    massGetCustomDataFieldByFieldName(fieldNames: string[], yearId: number, siteId: number): Observable<CSTField[]> {
        const queryString = '?yearId=' + yearId +
            '&siteId=' + siteId;
        return this.http.post<CSTField[]>(System.apiURL.Admin + 'MassGetCustomDataFieldByFieldName' + queryString, fieldNames);
    }

    massGetCustomDataFieldValue(siteYearId: number, fields: CSTField[]): Observable<CSTField[]> {
        const queryString = '?siteYearId=' + siteYearId;
        return this.http.post<CSTField[]>(System.apiURL.Admin + 'MassGetCustomDataFieldValue' + queryString, fields);
    }

    massUpdateCustomDataFieldValue(siteYearId: number, fields: CSTField[]): Observable<boolean> {
        const queryString = '?siteYearId=' + siteYearId;
        return this.http.post<boolean>(System.apiURL.Admin + 'MassUpdateCustomDataFieldValues' + queryString, fields);
    }

    getDesignPageByIDs(basePageOnly: boolean = false, pageIDs: number[] = [], includeAllUserPagesForID: boolean = false): Observable<CustomDesignPage[]> {
        const queryString = '?basePageOnly=' + basePageOnly +
            '&includeAllUserPagesForID=' + includeAllUserPagesForID;
        return this.http.post<CustomDesignPage[]>(System.apiURL.Admin + 'GetDesignPageByIDs' + queryString, pageIDs);
    }

    validateCustomField(field: CSTField, value: string, fieldRef: ElementRef<any> = null, isRequiredOverride: boolean = false, ignoreRequired: boolean = false): ErrorList[] {
        const errors: ErrorList[] = [];
        if (!field.ReadOnly || ((field.IsRequired || isRequiredOverride) && !ignoreRequired)) {
            if ((field.IsRequired || isRequiredOverride) && (value === null || value === undefined || value === '') && !ignoreRequired) {
                errors.push({
                    fieldName: field.DisplayName,
                    fieldRef: fieldRef,
                    errorMsg: 'is required.'
                });
            }
            let val = 0;
            if (value) {
                if (field.DataTypeID == CustomFieldDataTypeEnum.AlphaNumeric && !field.IsDataDef) {
                    val = value.length;
                    if (field.StartRange > 0 && val < field.StartRange) {
                        errors.push({
                            fieldName: field.DisplayName,
                            fieldRef: fieldRef,
                            errorMsg: 'must be at least ' + field.StartRange + 'characters long.'
                        });
                    }
                    if (field.EndRange > 0 && val > field.EndRange) {
                        errors.push({
                            fieldName: field.DisplayName,
                            fieldRef: fieldRef,
                            errorMsg: 'must not exceed ' + field.EndRange + 'characters in length.'
                        });
                    }
                }
                else if (field.DataTypeID == CustomFieldDataTypeEnum.Integer || field.DataTypeID == CustomFieldDataTypeEnum.Decimal) {
                    if (!isNaN(Number(value))) {
                        val = +value;
                    }
                    if (field.StartRange > 0 && val < field.StartRange) {
                        errors.push({
                            fieldName: field.DisplayName,
                            fieldRef: fieldRef,
                            errorMsg: 'must be greather than or equal to ' + field.StartRange + '.'
                        });
                    }
                    if (field.EndRange > 0 && val > field.EndRange) {
                        errors.push({
                            fieldName: field.DisplayName,
                            fieldRef: fieldRef,
                            errorMsg: 'must be less than or equal to ' + field.EndRange + '.'
                        });
                    }
                    if (field.DataTypeID == CustomFieldDataTypeEnum.Integer && (isNaN(Number(value)) || parseInt(Number(value).toString(), 10) != Number(value) || isNaN(parseInt(value, 10)))) {
                        errors.push({
                            fieldName: field.DisplayName,
                            fieldRef: fieldRef,
                            errorMsg: 'must be a whole number.'
                        });
                    }
                    if (field.DataTypeID == CustomFieldDataTypeEnum.Decimal) {
                        if (isNaN(Number(value))) {
                            errors.push({
                                fieldName: field.DisplayName,
                                fieldRef: fieldRef,
                                errorMsg: 'must be a valid number.'
                            });
                        }
                        else if (field.DecimalPrecision >= 0) {
                            let digitsAfterDecimal = 0;
                            const numberParts = value.toString().split('.');
                            if (numberParts.length > 1) {
                                digitsAfterDecimal = numberParts[1].length;
                            }
                            if (digitsAfterDecimal > field.DecimalPrecision) {
                                errors.push({
                                    fieldName: field.DisplayName,
                                    fieldRef: fieldRef,
                                    errorMsg: 'only allows ' + field.DecimalPrecision + ' digits after the decimal.'
                                });
                            }
                        }
                    }
                }
                else if (field.DataTypeID == CustomFieldDataTypeEnum.Telephone) {
                    // if (!telephoneDisplayvalue.match(System.phoneRegexp)) {
                    if (value.length != 10) {
                        errors.push({
                            fieldName: field.DisplayName,
                            fieldRef: fieldRef,
                            errorMsg: 'Invalid phone number.'
                        });
                    }
                }
                else if (field.DataTypeID == CustomFieldDataTypeEnum.Email) {
                    if (!value.match(System.mailRegexp)) {
                        errors.push({
                            fieldName: field.DisplayName,
                            fieldRef: fieldRef,
                            errorMsg: 'Invalid email address.'
                        });
                    }
                }
                else if (field.DataTypeID == CustomFieldDataTypeEnum.Time && !(value === null || value === undefined || value === '' || value == 'Invalid date')) {
                    if (!value.match(System.twelveHourTimeRegexp)) {
                        errors.push({
                            fieldName: field.DisplayName,
                            fieldRef: fieldRef,
                            errorMsg: 'must be in time format, hh:mm [AM|PM].'
                        });
                    }
                }
            }

        }
        return errors;
    }

    validateAllCustomFieldsInMemory(dataStore: CustomFieldDataObject[]): ErrorList[] {
        let errors: ErrorList[] = [];
        dataStore.forEach(f => {
            if (f.UserCanEdit) {
              const field = this.customDataFields.find(o => o.ID == f.CustomFieldID);
              errors = errors.concat(this.validateCustomField(field, f.Value));
            }
        });
        return errors;
    }

    // //// ***IMPORTANT*** ////
    // saveAllCustomFields should always be called from the tab group rather than the customizationFrameworkService unless absolutely necessary
    // For example this.tabGroup.saveAllCustomPages();
    // instead of this.customizationFrameworkService.saveAllCustomFieldsInMemory();
    // ///////////////////////////
    saveAllCustomFieldsInMemory(dataStore: CustomFieldDataObject[]): ErrorList[] {
        const errors = this.validateAllCustomFieldsInMemory(dataStore);
        if (errors.length == 0) {
            const fieldsToSave: CSTField[] = [];
            dataStore.filter(o => o.MasterId > 0 && o.Value != null && o.SiteYearId == this.yearService.defaultYear.SiteYearId && o.UserCanEdit && o.IsDirty).forEach(c => {
                const field = this.customDataFields.find(o => o.ID == c.CustomFieldID);
                const f = _.cloneDeep(field);
                f.Value = c.Value;
                f.MasterId = c.MasterId;
                fieldsToSave.push(f);
            });
            if (fieldsToSave.length > 0) {
                this.massUpdateCustomDataFieldValue(this.yearService.defaultYear.SiteYearId, fieldsToSave).pipe(take(1)).subscribe(o => { });
            }
        }
        return errors;
    }

    clearAllStoredCustomFieldData() {
        this.dataStores = {};
    }

    removeCustomFieldDataObject(dataStore: CustomFieldDataObject[], obj: CustomFieldDataObject) {
        const i = dataStore.findIndex(o => o.CustomFieldID == obj.CustomFieldID && o.MasterId == obj.MasterId && o.ObjectID == obj.ObjectID && o.SiteYearId == obj.SiteYearId);
        if (i >= 0) {
            dataStore = dataStore.splice(i, 1);
        }
    }

    clearStoredCustomFieldDataByObject(dataStore: CustomFieldDataObject[], objectId: number, masterId: number) {
        dataStore = dataStore.filter(o => o.ObjectID != objectId || (o.ObjectID == objectId && masterId != null && o.MasterId != masterId));
    }


    // //// ***IMPORTANT*** ////
    // updateMasterIDForPersistedData should always be called from the custom page tab group rather than the customizationFrameworkService unless absolutely necessary
    // For example this.tabGroup.updateMasterIDForPersistedData(householdContactObjectId, parent1.Id, -1);
    // instead of this.customizationFrameworkService.updateMasterIDForPersistedData(householdContactObjectId, parent1.Id, -1);
    // ///////////////////////////
    updateMasterIDForPersistedData(dataStore: CustomFieldDataObject[], objectId: number, newMasterId: number, oldMasterId?: number) {
        dataStore.forEach(f => {
            const field = this.customDataFields.find(o => o.ID == f.CustomFieldID && o.ObjectID == objectId && (oldMasterId == null || f.MasterId == oldMasterId));
            if (field) {
                f.MasterId = newMasterId;
            }
        });
    }

    userCanViewField(field: CSTField, editable: boolean, handleOwnData: boolean): boolean {
        let userCanView = false;
        if (field && field.Active) {
            userCanView = this.userCanEditField(field, editable, handleOwnData)
                || !field.IsSecured
                || field.IsRequired
                || this.securityService.IsAllPermissionCodeAssigned('CST_VAUF')
                || this.securityService.IsAnyPermissionGroupAssigned(...field.ViewPermissionGroupIDs);
        }
        return userCanView;
    }

    userCanEditField(field: CSTField, editable: boolean, handleOwnData: boolean): boolean {
        let userCanEdit = false;
        if (field && field.Active && editable && !field.ReadOnly
            && (!handleOwnData || (handleOwnData && (!field.IsProcedureBased || field.WriteSPName)))
        ) {
            userCanEdit = !field.IsSecured
                || this.securityService.IsAllPermissionCodeAssigned('CST_MAUF')
                || this.securityService.IsAllPermissionCodeAssigned('CST_MACP')
                || this.securityService.IsAnyPermissionGroupAssigned(...field.ModifyPermissionGroupIDS);
        }
        return userCanEdit;
    }

    getCustomPageSystemTemplateByID(pageId: number): Observable<CSTCustomPage> {
        const queryString = '?pageId=' + pageId;
        return this.http.get<CSTCustomPage>(System.apiURL.Admin + 'GetCustomPageSystemTemplateByID' + queryString);
    }

    reconcileChangesToCustomPage(customPage: CSTCustomPage): Observable<EFormDataObject> {
        let formData = new EFormDataObject();
        try {
            formData = JSON.parse(customPage.FormData);

            // need to reconcile any changes to the system version of this page to the local version
            if (customPage && customPage.IsSysGen && customPage.LocalVersion != customPage.SystemVersion) {
                return this.getCustomPageSystemTemplateByID(customPage.ID).pipe(map(systemPage => {
                    let systemFormData = new EFormDataObject();
                    try {
                        systemFormData = JSON.parse(systemPage.FormData);
                    } catch (err) {
                        systemFormData = new EFormDataObject();
                    }
                    return this.compareSystemPageToLocalPage(systemFormData, formData);
                }));
            }
            else {
                return of(formData);
            }


        } catch (err) {
            formData = new EFormDataObject();
            return of(formData);
        }
    }

    reconcileChangesToDesignPage(designPage: CustomDesignPage): Observable<EFormDataObject> {
        let formData = new EFormDataObject();
        try {
            formData = JSON.parse(designPage.FormData);

            // need to reconcile any changes to the system version of this page to the local version
            if (designPage && designPage.Version != designPage.BaseVersion) {
                return this.getDesignPageByIDs(true, [designPage.ID], false).pipe(map(systemPages => {
                    const systemPage = systemPages.find(o => o.BasePage);
                    let systemFormData = new EFormDataObject();
                    try {
                        systemFormData = JSON.parse(systemPage.FormData);
                    } catch (err) {
                        systemFormData = new EFormDataObject();
                    }
                    return this.compareSystemPageToLocalPage(systemFormData, formData);
                }));
            }
            else {
                return of(formData);
            }


        } catch (err) {
            formData = new EFormDataObject();
            return of(formData);
        }
    }

    compareSystemPageToLocalPage(systemFormData: EFormDataObject, localFormData: EFormDataObject): EFormDataObject {
        const systemPageControls = this.eFormService.getAllFormControls(systemFormData);
        const localPageControls = this.eFormService.getAllFormControls(localFormData);
        const newSystemControls: EFormControl[] = [];
        systemPageControls.forEach(sysControl => {
            if (sysControl.CustomField && sysControl.CustomFieldName && !localPageControls.find(o => o.CustomFieldName == sysControl.CustomFieldName)) {
                newSystemControls.push(sysControl);
            }
        });
        if (newSystemControls.length > 0 && localFormData && localFormData.Tabs.length > 0 && localFormData.Tabs[0].Pages.length > 0 && localFormData.Tabs[0].Pages[0].Columns.length > 0) {
            const col = localFormData.Tabs[0].Pages[0].Columns[0];
            if (col.Rows.length > 0) {
                newSystemControls.forEach(c => {
                    const newRow = new EFormRow();
                    newRow.Controls = [];
                    newRow.Controls.push(c);
                    col.Rows.push(newRow);
                });
            }
            else {
                newSystemControls.forEach(c => {
                    col.Controls.push(c);
                });
            }
        }
        return localFormData;
    }

    getLinkedCustomFields(formData: EFormDataObject, eForm: EForm): Observable<CSTField[]> {
        const newValues: CSTField[] = [];
        if (this.loginService.LoginSettings.EnableCustomizationFramework && this.loginService.LoginSettings.C360CustomFieldsImported && eForm.TemplateType == 'student') {
            const controls = this.eFormService.getAllFormControls(formData);
            const fieldNames = controls.filter(o => o.LinkedCSTFieldName).map(o => o.LinkedCSTFieldName);
            return this.massGetCustomDataFieldByFieldName(fieldNames, this.yearService.defaultYear.Id, this.yearService.defaultYear.SchoolID).pipe(take(1)).pipe(map(fields => {
                controls.forEach(control => {
                    if (control.LinkedCSTFieldName) {
                        const dbField = fields.find(o => o.FieldName == control.LinkedCSTFieldName);
                        if (dbField) {
                            if (control.Value) {
                                dbField.Value = control.Value.toString();
                            }
                            dbField.MasterId = eForm.Student_ID;
                            newValues.push(dbField);
                        }
                    }
                });
                return newValues;
            }));

        }
        return of(newValues);
    }

    persistValue(field: CSTField, editableOverride: boolean, allowControlToHandleOwnData: boolean, masterId: number, dataStore: CustomFieldDataObject[], isDirty: boolean) {
        if (this.userCanViewField(field, editableOverride, allowControlToHandleOwnData)) {
            let dataObj = this.getCustomFieldDataObject(field, masterId, dataStore);
            if (!dataObj) {
                dataObj = new CustomFieldDataObject();
                dataObj.CustomFieldID = field.ID;
                dataObj.MasterId = masterId;
                dataObj.SiteYearId = this.yearService.defaultYear.SiteYearId;
                dataObj.Value = field.Value;
                dataObj.ObjectID = field.ObjectID;
                dataObj.UserCanEdit = this.userCanEditField(field, editableOverride, allowControlToHandleOwnData);
                dataObj.IsDirty = !!isDirty;
                if (dataStore) {
                    dataStore.push(dataObj);
                }
            }
            else {
                dataObj.Value = field.Value;
                dataObj.IsDirty = isDirty || dataObj.IsDirty;
                dataObj.UserCanEdit = dataObj.IsDirty || this.userCanEditField(field, editableOverride, allowControlToHandleOwnData);
            }
        }
    }

    getCustomFieldDataObject(field: CSTField, masterId: number, dataStore: CustomFieldDataObject[]): CustomFieldDataObject {
        if (dataStore) {
            return dataStore.find(o => o.CustomFieldID == field.ID && o.MasterId == masterId && o.SiteYearId == this.yearService.defaultYear.SiteYearId);
        }
        return null;
    }

    getNewDataStore(): CustomFieldDataObject[] {
        const dataSource: CustomFieldDataObject[] = [];
        let nextIndex = 0;
        if (Object.keys(this.dataStores).length > 0) {
            nextIndex = Math.max.apply(null, Object.keys(this.dataStores));
        }
        this.dataStores[nextIndex] = dataSource;
        return dataSource;
    }

}